// Efeitos das notificações do SOCIETÁRIO.
// Registrado em `constants/notificationHandlers.ts`.

import type { QueryClient } from '@tanstack/react-query';
import type {
  DestinoDeNotificacao,
  MapaDeHandlers,
  NotificacaoParaDestino,
} from '../../constants/notificationHandlers';
import { emitSocietarioGerado } from './eventos';

/**
 * O que o SOCIETARIO le' da mensagem que chega pelo WebSocket.
 *
 * O `engine` diz QUAL motor gerou — e' por ele, e nao pelo `tipo`, que se
 * distingue v2 de v3 (o tipo `societario_contrato_gerado_v2` e' historico e
 * serve os dois; ver o handler abaixo). Sem union de literais de proposito:
 * os valores reais que o backend manda hoje sao `v3-alteracao-consolidacao` e
 * `v3-transformacao-consolidacao`, e uma lista fechada aqui envelhece a cada
 * motor novo sem ninguem perceber.
 */
export interface DadosWsSocietario {
  contrato_id?: number;
  status?: string;
  url?: string;
  decisoes_ia_count?: number;
  cost_estimate_usd?: number;
  error?: string;
  engine?: string;
}

/** O que o SOCIETARIO le' do `dados_json` PERSISTIDO (o que o painel lista). */
export interface DadosJsonSocietario {
  contrato_id?: number;
}

/**
 * As listas que qualquer mudança de contrato societário torna obsoletas.
 *
 * ⚠️ Ficam numa constante porque as três apareciam repetidas em dois ramos
 * distintos do provider antigo (geração própria e evento de departamento) —
 * duas cópias que precisavam concordar e não tinham nada garantindo isso.
 */
function invalidarListasDeContratos(queryClient: QueryClient, contratoId?: number): void {
  if (contratoId) {
    queryClient.invalidateQueries({ queryKey: ['contrato', contratoId] });
  }
  queryClient.invalidateQueries({ queryKey: ['societario-contratos'] });
  queryClient.invalidateQueries({ queryKey: ['societario-contratos-preview'] });
  queryClient.invalidateQueries({ queryKey: ['societario-contratos-empresa'] });
}

/**
 * Evento de departamento: outro operador gerou, atualizou ou arquivou.
 *
 * Reflete nas listas e no detalhe, mas **não** dispara o emitter de geração —
 * o modal de acompanhamento é do operador que clicou em gerar, e abrir a tela
 * dele por causa da ação de um colega seria errado.
 */
const refletirEventoDeOutroOperador: MapaDeHandlers[string] = ({ message, queryClient }) => {
  invalidarListasDeContratos(queryClient, message.data?.contrato_id);
};

export const HANDLERS_SOCIETARIO: MapaDeHandlers = {
  /**
   * Geração assíncrona concluiu (o canal que o modal de acompanhamento assina).
   *
   * ⚠️ A CHAVE mantém o sufixo `_v2` por compatibilidade — nasceu no engine v2
   * e desde 2026-08-19 a geração assíncrona do v3 usa o MESMO caminho, com o
   * motor indo no campo `engine` do payload. Não é engano.
   */
  societario_contrato_gerado_v2: ({ message, queryClient }) => {
    const d = message.data;
    if (d.contrato_id) {
      emitSocietarioGerado({
        contratoId: d.contrato_id,
        status: d.status ?? 'gerado',
        url: d.url,
        decisoesIaCount: d.decisoes_ia_count,
        costEstimateUsd: d.cost_estimate_usd,
        error: d.error,
        engine: d.engine,
      });
    }
    invalidarListasDeContratos(queryClient, d.contrato_id);
  },

  societario_contrato_gerado_v2_dept: refletirEventoDeOutroOperador,
  societario_rascunho_atualizado: refletirEventoDeOutroOperador,
  societario_contrato_arquivado: refletirEventoDeOutroOperador,
};

/**
 * Destino do clique: contrato societário abre a listagem com o card destacado.
 *
 * ⚠️ O DOMÍNIO é reconhecido ANTES do id. O código anterior fazia
 *
 *     dados_json.contrato_id ?? (referencia_tipo === 'contrato_societario' ? … )
 *
 * e o `??` só avalia o lado direito quando o esquerdo é nulo — então o guard de
 * tipo nunca rodava para notificação que já trouxesse `contrato_id`. E
 * `contrato_campos_pendentes`, que é de contrato de EVENTO, traz exatamente
 * isso (`endpoints/eventos.py`), com `referencia_tipo='contrato'`.
 *
 * `tb_contrato` e `tb_contrato_societario` têm autoincrement independente: em
 * id baixo a colisão abria o contrato societário ERRADO, com o botão de remover
 * ao lado.
 *
 * Qualquer um dos dois sinais serve: o backend grava
 * `referencia_tipo='contrato_societario'`, e os tipos do societário são
 * prefixados. Notificação de evento não casa nenhum dos dois.
 */
// ⚠️ `function` nomeada, e não arrow atribuída a const: o plugin react do
// eslint trata arrow-com-destructuring como componente e cobra PropTypes de
// cada propriedade. Isto é função pura de dado, sem JSX no arquivo inteiro.
function destinoSocietario({
  tipo,
  referencia_tipo: referenciaTipo,
  referencia_id: referenciaId,
  dados_json: dados,
}: NotificacaoParaDestino): string | null {
  const ehContratoSocietario =
    referenciaTipo === 'contrato_societario' || String(tipo ?? '').startsWith('societario_');
  if (!ehContratoSocietario) return null;

  const contratoId = dados?.contrato_id ?? referenciaId;
  return contratoId ? `/societario/contratos?destaque=${contratoId}` : null;
}

export const DESTINO_SOCIETARIO: DestinoDeNotificacao = destinoSocietario;
